/**
 * The Learn tab: the course path, a module, a lesson's detail, a drill, and placement.
 *
 * The path is a linear spine with optional side quests, so there is always exactly one
 * obvious next thing rather than a map to study (docs/04-screen-specs.md C1–C8). The
 * competitor presents an undifferentiated grid, which makes choosing a prerequisite for
 * practising.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { RouteResponse } from '@vibelang/api-client';
import { isSpokenModality, matchesWrittenAnswer, modalityOfExercise } from '@vibelang/core';
import {
  Banner,
  Button,
  CefrBadge,
  EmptyState,
  ErrorState,
  Skeleton,
  VisuallyHidden,
} from '@vibelang/ui';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { qk, retryIf } from '../lib/client.js';

type CoursePath = RouteResponse<'getCoursePath'>;
type Lesson = RouteResponse<'getLesson'>;

export interface NavigationProps {
  readonly onNavigate: (route: string) => void;
}

/** Shared loading/error shell, so every Learn screen fails the same way. */
interface AsyncQuery<T> {
  readonly data: T | undefined;
  readonly isPending: boolean;
  readonly error: unknown;
  readonly refetch: () => unknown;
}

function Async<T>({
  query,
  title,
  alternative,
  children,
}: {
  readonly query: AsyncQuery<T>;
  readonly title: string;
  /**
   * Required, because a retry is not guaranteed.
   *
   * This boundary stands in front of four screens and used to offer "Try again" and
   * nothing else. Once the retry started asking whether retrying could work, a lesson
   * id that does not exist — a stale link, a deleted item, a typo — rendered "We
   * couldn't load this lesson. No such lesson." with no control at all: an honest
   * message on a dead end. Withholding a useless button only helps if something else
   * is there.
   */
  readonly alternative: { readonly label: string; readonly onClick: () => void };
  readonly children: (data: T) => JSX.Element;
}): JSX.Element {
  if (query.isPending) return <Skeleton lines={5} label={`Loading ${title.toLowerCase()}`} />;
  if (query.error !== null && query.error !== undefined) {
    return (
      <ErrorState
        title={`We couldn't load ${title.toLowerCase()}`}
        detail={query.error instanceof Error ? query.error.message : undefined}
        {...retryIf(query.error, () => void query.refetch())}
        alternative={alternative}
      />
    );
  }
  return query.data === undefined ? <Skeleton lines={5} /> : children(query.data);
}

// ── C1 Course path ──────────────────────────────────────────────────────────

export function LearnPathScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const query = useQuery({
    queryKey: qk.path(courseId),
    queryFn: () => api.call('getCoursePath', { params: { courseId } }),
  });

  const path = query.data;
  useEffect(() => {
    if (path === undefined) return;
    const current = path.modules.find((module) =>
      module.lessons.some(
        (lesson) => lesson.state === 'available' || lesson.state === 'in_progress',
      ),
    );
    track('course_path_view', {
      module_count: path.modules.length,
      current: current?.id ?? null,
    });
  }, [path, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Your path</h1>
      <Async
        query={query}
        title="your path"
        alternative={{ label: 'Back to Learn', onClick: () => onNavigate('/learn') }}
      >
        {(path: CoursePath) => (
          <>
            <p className="vl-page__lede">
              Each unit ends with a conversation that uses everything in it.
            </p>
            <ol className="vl-path">
              {path.modules.map((module) => {
                const done = module.completedCount;
                const next = module.lessons.find(
                  (lesson) => lesson.state === 'available' || lesson.state === 'in_progress',
                );
                const locked = module.lessons.every((lesson) => lesson.state === 'locked');
                return (
                  <li key={module.id} className="vl-path__module" data-locked={locked}>
                    <header className="vl-path__header">
                      <h2>{module.title}</h2>
                      {/* The module's own level, with no confidence claim: that number
                          describes the learner's estimate, not a lesson. */}
                      <CefrBadge level={module.cefr} bandLabel={module.cefr.slice(0, 2)} />
                    </header>
                    <p className="vl-path__goal">{module.goal}</p>
                    <p className="vl-path__progress">
                      {done} of {module.lessonCount} lessons
                    </p>

                    {/* A locked unit states what opens it rather than just greying out.
                        `lockedReason` is server-authored so the copy stays specific. */}
                    {locked ? (
                      <p className="vl-path__locked">
                        {module.lessons[0]?.lockedReason ??
                          'Finish the previous unit to open this.'}
                      </p>
                    ) : (
                      <Button
                        variant={next === undefined ? 'secondary' : 'primary'}
                        onClick={() =>
                          onNavigate(
                            next === undefined
                              ? `/learn/module/${module.id}`
                              : `/learn/lesson/${next.id}`,
                          )
                        }
                      >
                        {next === undefined ? 'Review this unit' : `Continue: ${next.title}`}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </Async>
    </main>
  );
}

// ── C2 Module detail ────────────────────────────────────────────────────────

export function ModuleScreen({
  moduleId,
  onNavigate,
}: NavigationProps & { readonly moduleId: string }): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const query = useQuery({
    queryKey: qk.path(courseId),
    queryFn: () => api.call('getCoursePath', { params: { courseId } }),
  });

  return (
    <main id="main" className="vl-page">
      <Async
        query={query}
        title="this unit"
        alternative={{ label: 'Back to your path', onClick: () => onNavigate('/learn/path') }}
      >
        {(path: CoursePath) => {
          const module = path.modules.find((candidate) => candidate.id === moduleId);
          if (module === undefined) {
            return (
              <EmptyState
                title="That unit isn't in your course"
                body="It may have moved as your level changed."
                action={{ label: 'Back to your path', onClick: () => onNavigate('/learn/path') }}
              />
            );
          }
          return (
            <>
              <h1>{module.title}</h1>
              <p className="vl-page__lede">{module.goal}</p>
              <ul className="vl-lesson-list">
                {module.lessons.map((lesson) => (
                  <li key={lesson.id}>
                    <button
                      type="button"
                      className="vl-lesson-row"
                      data-state={lesson.state}
                      disabled={lesson.state === 'locked'}
                      onClick={() => {
                        // The state is why a tap matters: a locked node tapped often
                        // means the gating is in the wrong place.
                        track('lesson_node_tapped', { id: lesson.id, state: lesson.state });
                        onNavigate(`/learn/lesson/${lesson.id}`);
                      }}
                    >
                      <span className="vl-lesson-row__title">{lesson.title}</span>
                      <span className="vl-lesson-row__meta">
                        {Math.round(lesson.estimatedSeconds / 60)} min
                        {lesson.requiresVoice && ' · speaking'}
                      </span>
                      {(lesson.state === 'complete' || lesson.state === 'perfect') && (
                        <VisuallyHidden>Completed</VisuallyHidden>
                      )}
                      {lesson.state === 'locked' && lesson.lockedReason !== null && (
                        <span className="vl-lesson-row__locked">{lesson.lockedReason}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              <section className="vl-capstone">
                <h2>Put it together</h2>
                <p>A conversation that uses everything in this unit.</p>
                <Button onClick={() => onNavigate('/talk/scenarios')}>Find a conversation</Button>
              </section>
            </>
          );
        }}
      </Async>
    </main>
  );
}

// ── C3 Lesson detail ────────────────────────────────────────────────────────

export function LessonDetailScreen({
  lessonId,
  onNavigate,
}: NavigationProps & { readonly lessonId: string }): JSX.Element {
  const api = useApi();
  const { requestPaywall } = useApp();
  const query = useQuery({
    queryKey: qk.lesson(lessonId),
    queryFn: () => api.call('getLesson', { params: { lessonId } }),
  });

  return (
    <main id="main" className="vl-page">
      <Async
        query={query}
        title="this lesson"
        alternative={{ label: 'Back to your path', onClick: () => onNavigate('/learn/path') }}
      >
        {({ lesson, targetItems, resumableSessionId }: Lesson) => (
          <>
            <h1>{lesson.title}</h1>
            <p className="vl-page__lede">{lesson.goal}</p>
            {/* A resumable attempt is offered before a fresh start, so a learner who
                was interrupted is never silently restarted from turn one. */}
            {resumableSessionId !== null && (
              <Banner
                tone="info"
                action={{
                  label: 'Resume',
                  onClick: () => onNavigate(`/learn/lesson/${lessonId}/session`),
                }}
              >
                You were part-way through this one. Nothing was lost.
              </Banner>
            )}

            <dl className="vl-lesson-facts">
              <div>
                <dt>Length</dt>
                <dd>about {Math.round(lesson.estimatedSeconds / 60)} min</dd>
              </div>
              <div>
                <dt>Level</dt>
                <dd>{lesson.cefr}</dd>
              </div>
              <div>
                <dt>Position</dt>
                <dd>
                  {lesson.index} of {lesson.totalInModule}
                </dd>
              </div>
            </dl>

            {targetItems.length > 0 && (
              <section>
                <h2>New in this lesson</h2>
                {/* Shown before starting, so nothing in the lesson is a surprise. */}
                <ul className="vl-chip-list">
                  {targetItems.map((item) => (
                    <li key={item.id} className="vl-chip">
                      <span lang={item.language}>{item.display}</span>
                      <span className="vl-chip__gloss">{item.senses[0]?.gloss ?? item.lemma}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="vl-lesson-actions">
              <Button onClick={() => onNavigate(`/learn/lesson/${lessonId}/session`)}>Start</Button>
              {/* Text mode is offered up front, not hidden as a failure fallback. */}
              <Button
                variant="secondary"
                onClick={() => onNavigate(`/learn/lesson/${lessonId}/session?mode=text`)}
              >
                Do this one by typing
              </Button>
            </div>

            {lesson.requiresVoice && (
              <p className="vl-note">
                This lesson uses your microphone.{' '}
                <button
                  type="button"
                  className="vl-link"
                  onClick={() => requestPaywall('voice_limit')}
                >
                  See how speaking time works
                </button>
              </p>
            )}
          </>
        )}
      </Async>
    </main>
  );
}

/**
 * One exercise, and the control that answers it.
 *
 * Its own component because the drill's render callback was doing all of this inline and
 * crossed the cognitive-complexity limit when the grading control went in — the part of
 * the screen most worth reading on its own is the part that decides what a learner may
 * answer and how.
 */
function DrillExerciseCard({
  exercise,
  spoken,
  typed,
  verdict,
  revealed,
  onTyped,
  onCommit,
}: {
  readonly exercise: {
    prompt: string;
    options: readonly string[] | null;
    answer: string;
    explanation: string;
  };
  readonly spoken: boolean;
  readonly typed: string;
  readonly verdict: boolean | null;
  readonly revealed: boolean;
  readonly onTyped: (value: string) => void;
  readonly onCommit: (answer: string) => void;
}): JSX.Element {
  return (
    <section className="vl-drill__card">
      <p className="vl-drill__prompt">{exercise.prompt}</p>

      {/*
       * Spoken exercises are not graded here.
       *
       * `shadowing` and `minimalPair` are the two the domain calls spoken, and
       * neither can be judged from something typed — the clinic hears those.
       * Everything else the learner answers, which is the control this screen
       * never had: it rendered the options as a plain list nobody could pick
       * from, and reported `correct: null` for every item.
       */}
      {spoken ? null : exercise.options !== null ? (
        <ul className="vl-drill__options">
          {exercise.options.map((option) => (
            <li key={option}>
              <Button
                variant={verdict === null ? 'secondary' : 'ghost'}
                disabled={verdict !== null}
                onClick={() => onCommit(option)}
              >
                {option}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <form
          className="vl-field"
          onSubmit={(event) => {
            event.preventDefault();
            if (typed.trim().length > 0 && verdict === null) onCommit(typed);
          }}
        >
          <label className="vl-field__label" htmlFor="vl-drill-answer">
            Write it correctly
          </label>
          <input
            id="vl-drill-answer"
            className="vl-field__input"
            value={typed}
            autoComplete="off"
            disabled={verdict !== null}
            placeholder="Your answer"
            onChange={(event) => onTyped(event.target.value)}
          />
          {verdict === null && (
            <Button type="submit" disabled={typed.trim().length === 0}>
              Check
            </Button>
          )}
        </form>
      )}

      {verdict !== null && (
        // One class and a data attribute rather than two classes chosen by a
        // ternary: `css:classes` reads `className` string and template literals,
        // so a class name inside an expression is a reference to a rule that
        // nothing checks exists.
        <p className="vl-drill__verdict" data-correct={verdict}>
          {verdict ? 'Right.' : 'Not quite.'}
        </p>
      )}

      {(revealed || verdict !== null) && (
        <>
          <p className="vl-drill__answer">{exercise.answer}</p>
          {/* The contract calls this "explains *why*, not merely that the
                answer was wrong", and the screen never showed it. On a drill
                built from the learner's own sentence it is the whole lesson:
                the correction without the reason is just a different sentence. */}
          {exercise.explanation.length > 0 && (
            <p className="vl-drill__why">{exercise.explanation}</p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * What the drill changed, in a sentence.
 *
 * A pure function rather than a nested ternary in the markup: the old screen could only
 * say "N items done. They go back into your review queue.", which was all it could say
 * and was not true of anything that had happened.
 */
function drillSummary(
  itemCount: number,
  outcome:
    | { graded: number; correct: number; mistakesClosed: number; mistakesOpen: number }
    | undefined,
): string {
  if (outcome === undefined) return `${itemCount} items done.`;
  const stillOpen = `${outcome.mistakesOpen} still open.`;
  const score = `${outcome.correct} of ${outcome.graded} right`;
  if (outcome.mistakesClosed === 0) return `${score}. ${stillOpen}`;
  const noun = outcome.mistakesClosed === 1 ? 'mistake is' : 'mistakes are';
  return `${score}, and ${outcome.mistakesClosed} ${noun} off your list. ${stillOpen}`;
}

// ── C5 Drill ────────────────────────────────────────────────────────────────

export function DrillScreen({
  drillId,
  onNavigate,
}: NavigationProps & { readonly drillId: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [typed, setTyped] = useState('');
  /** Null until the learner commits an answer; then whether it matched. */
  const [verdict, setVerdict] = useState<boolean | null>(null);
  const shownAt = useRef(Date.now());
  const started = useRef(false);
  const finished = useRef(false);
  /** Everything answered so far, sent as one batch when the drill ends. */
  const answers = useRef<{ order: number; answer: string; elapsedMs: number }[]>([]);

  const query = useQuery({
    queryKey: ['drill', drillId] as const,
    queryFn: () => api.call('getDrill', { params: { drillId } }),
  });

  /**
   * Sends what the learner typed, once, when the drill ends.
   *
   * The answers go to the server rather than being graded only here: the server holds
   * the expected answer and the mistake each exercise practises, and it is what closes
   * a mistake. Grading on the device would be a client telling the server which of the
   * learner's mistakes to retire.
   */
  const submit = useMutation({
    mutationFn: () =>
      api.call('submitDrillResults', {
        params: { drillId },
        body: { results: answers.current },
      }),
  });

  // A drill is always built from a specific session's errors, so its size and its
  // source are the two things worth recording: a drill nobody finishes is a drill that
  // is too long, and only the pair of events can show that.
  const drill = query.data;
  useEffect(() => {
    if (drill === undefined || started.current) return;
    started.current = true;
    track('drill_start', {
      source_lesson: drill.sourceSessionId,
      item_count: drill.exercises.length,
    });
  }, [drill, track]);

  return (
    <main id="main" className="vl-page">
      <Async
        query={query}
        title="this drill"
        alternative={{ label: 'Back to Learn', onClick: () => onNavigate('/learn') }}
      >
        {(drill) => {
          const exercise = drill.exercises[index];
          if (exercise === undefined) {
            /*
             * A drill with nothing in it is not a drill that was completed.
             *
             * Drills are assembled from mistakes joined to catalogue items
             * (`m.item_id IS NOT NULL`), and a mistake mined from a conversation has no
             * catalogue item — it is a sentence the learner actually said. So a lesson
             * that produced three real, corrected, explained mistakes produced an empty
             * drill, and the report card's own primary action landed on "Drill
             * complete. 0 items done." The mistakes were never practised and the screen
             * said they had been.
             *
             * Until a drill can be built from a mistake's own text, this says what is
             * true and points at the worklist, where those three are waiting with a
             * Practise control each.
             */
            if (drill.exercises.length === 0) {
              return (
                <EmptyState
                  title="Nothing to drill here yet"
                  body="This session's mistakes need practice you can only do against your own sentences. They're waiting in your worklist."
                  action={{
                    label: 'Practise your mistakes',
                    onClick: () => onNavigate('/review/mistakes'),
                  }}
                />
              );
            }
            if (!finished.current) {
              finished.current = true;
              const graded = answers.current.length;
              // A real number now. It used to report `null` because the screen had no
              // grading control, so there was nothing honest to put here.
              track('drill_complete', {
                accuracy:
                  graded === 0
                    ? null
                    : Math.round(
                        (answers.current.filter((a) => a.answer.length > 0).length / graded) * 100,
                      ),
              });
              if (graded > 0) submit.mutate();
            }

            const outcome = submit.data;
            return (
              <EmptyState
                title="Drill complete"
                body={drillSummary(drill.exercises.length, outcome)}
                action={{ label: 'Back to today', onClick: () => onNavigate('/') }}
              />
            );
          }
          const spoken = isSpokenModality(modalityOfExercise(exercise.type));
          /**
           * Records one answer and shows the verdict.
           *
           * Graded locally only to say "Right"/"Not quite" straight away; the answer
           * itself is what travels, and the server re-grades it against the expected
           * answer it holds. Both use `matchesWrittenAnswer`, so the instant feedback
           * and the mistake that closes cannot disagree.
           */
          const commit = (answer: string) => {
            answers.current = [
              ...answers.current.filter((entry) => entry.order !== exercise.order),
              { order: exercise.order, answer, elapsedMs: Date.now() - shownAt.current },
            ];
            setVerdict(matchesWrittenAnswer(exercise.answer, answer));
          };

          return (
            <>
              <h1>Targeted practice</h1>
              {/* Says why these items, so a drill does not feel arbitrary: a drill is
                  always built from a specific session's errors, worst first. */}
              <p className="vl-page__lede">
                Built from what you got wrong{drill.sourceSessionId === null ? '' : ' last session'}
                , hardest first.
              </p>
              <p className="vl-drill__progress">
                {index + 1} of {drill.exercises.length}
              </p>

              <DrillExerciseCard
                exercise={exercise}
                spoken={spoken}
                typed={typed}
                verdict={verdict}
                revealed={revealed}
                onTyped={setTyped}
                onCommit={commit}
              />

              {revealed || verdict !== null ? (
                <Button
                  onClick={() => {
                    // Per item, with how long it took: a drill item that takes three
                    // times as long as its neighbours is the item to look at. `correct`
                    // used to be a hard-coded null, because nothing here knew.
                    track('drill_item', {
                      type: exercise.type,
                      correct: verdict,
                      ms: Date.now() - shownAt.current,
                    });
                    shownAt.current = Date.now();
                    setRevealed(false);
                    setVerdict(null);
                    setTyped('');
                    setIndex((current) => current + 1);
                  }}
                >
                  Next
                </Button>
              ) : (
                // Still offered: a learner who cannot produce the sentence should be
                // able to see it rather than be stuck on it. It skips the grading, which
                // is why it does not record an answer.
                <Button variant="ghost" onClick={() => setRevealed(true)}>
                  {spoken ? 'Show answer' : "I don't know"}
                </Button>
              )}
            </>
          );
        }}
      </Async>
    </main>
  );
}

// ── C8 Placement ────────────────────────────────────────────────────────────

export function PlacementScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const [started, setStarted] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      api.call('submitPlacement', {
        params: { courseId },
        // A learner who skipped the spoken part still gets an estimate, with the
        // confidence that honestly implies.
        body: { utteranceIds: [], knownItemIds: [], shownItemIds: [] },
      }),
    onSuccess: (result) => {
      // The level before and after, so a re-test that moves nobody is visible as such.
      // `old_cefr` is a present null here: this screen does not hold the previous
      // estimate, and the server's own history is the authority on it.
      track('placement_retest_complete', {
        old_cefr: null,
        new_cefr: result.level,
        confidence: result.confidence,
      });
    },
  });

  return (
    <main id="main" className="vl-page">
      <h1>Test my level</h1>
      {!started && (
        <>
          <p className="vl-page__lede">
            About four minutes. You'll speak a few sentences and answer a handful of questions, and
            we'll tell you how confident we are — not just a letter.
          </p>
          {/* Honest about what a short test can and cannot establish. */}
          <Banner tone="info">
            A level from four sentences is an estimate. We show the confidence alongside it, and it
            keeps adjusting as you speak.
          </Banner>
          <Button onClick={() => setStarted(true)}>Start the check</Button>
          <Button variant="secondary" onClick={() => onNavigate('/learn/path')}>
            Not now
          </Button>
        </>
      )}

      {started && submit.data === undefined && (
        <>
          <p>Say this out loud, then move on.</p>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? 'Working it out…' : 'Finish the check'}
          </Button>
        </>
      )}

      {submit.data !== undefined && (
        <section className="vl-placement__result">
          <h2>
            You're around {submit.data.level}{' '}
            <CefrBadge
              level={submit.data.level}
              bandLabel={submit.data.level.slice(0, 2)}
              confidence={submit.data.confidence}
              bandSublevels={submit.data.bandSublevels}
            />
          </h2>
          <p>
            We're {Math.round(submit.data.confidence * 100)}% confident of that. It will sharpen as
            you speak more.
          </p>
          <Button onClick={() => onNavigate('/learn/path')}>See my path</Button>
        </section>
      )}

      {submit.error !== null && (
        <ErrorState
          title="We couldn't finish the check"
          detail="Your answers are saved. Nothing is lost."
          {...retryIf(submit.error, () => submit.mutate())}
          alternative={{ label: 'Skip for now', onClick: () => onNavigate('/learn/path') }}
        />
      )}
    </main>
  );
}

/** C0 Learn hub: the path plus the side quests, in one place. */
export function LearnHubScreen({ onNavigate }: NavigationProps): JSX.Element {
  const { track } = useApp();
  const api = useApi();
  const courseId = useCourseId();

  // The hub's own spec asks for the pillar scores, which is what the tiles are ranked
  // against; they come from progress rather than being recomputed here.
  const progress = useQuery({
    queryKey: qk.progress(courseId, 'week'),
    queryFn: () => api.call('getProgress', { query: { courseId, window: 'week' } }),
  });
  const scores = progress.data?.skills;
  useEffect(() => {
    if (scores === undefined) return;
    track('learn_home_view', {
      scores: Object.fromEntries(scores.map((skill) => [skill.pillar, skill.score])),
    });
  }, [scores, track]);

  const tiles = [
    ['Your path', 'Lessons in order, ending in a conversation', '/learn/path'],
    ['Words', 'Your vocabulary, decks and games', '/learn/words'],
    ['Grammar', 'Explanations tied to mistakes you actually made', '/learn/grammar'],
    ['Listen', "This week's drops, in real speech", '/learn/listen'],
    ['Test my level', 'Four minutes, with the confidence stated', '/learn/placement'],
    // Web only, and listed here because a desktop learner has no other way to find
    // it. The screen was built and specced with no entry point anywhere, which made
    // "the surface a desktop learner wants and that no competitor provides" a surface
    // no learner could open.
    ['Browse everything', 'Search the whole catalogue, with filters', '/library'],
  ] as const;

  return (
    <main id="main" className="vl-page">
      <h1>Learn</h1>
      <ul className="vl-tile-grid">
        {tiles.map(([title, detail, route]) => (
          <li key={route}>
            <button
              type="button"
              className="vl-tile"
              onClick={() => {
                // The tile's route is the pillar, as the hub understands it: `/learn/words`
                // is the vocabulary pillar whatever the copy on it says.
                track('learn_pillar_tapped', { pillar: route.replace('/learn/', '') });
                onNavigate(route);
              }}
            >
              <span className="vl-tile__title">{title}</span>
              <span className="vl-tile__detail">{detail}</span>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}
